import { apiClient } from './client';

export interface ColorPalette {
  primary_gradient_start: string;
  primary_gradient_end: string;
  secondary_gradient_start: string;
  secondary_gradient_end: string;
  primary_button_gradient_start: string;
  primary_button_gradient_end: string;
  gradient_accent_start?: string;
  primary_button_bg: string;
  primary_button_hover: string;
  secondary_button_bg: string;
  secondary_button_hover: string;
  primary_text: string;
  secondary_text: string;
  accent_text: string;
  card_background: string;
  border_color: string;

  // Status colors (semantic)
  success?: string;
  warning?: string;
  error?: string;
  info?: string;

  // Chart colors (data visualization)
  chart_1?: string;
  chart_2?: string;
  chart_3?: string;
  chart_4?: string;
  chart_5?: string;
  chart_6?: string;
  chart_7?: string;
  chart_8?: string;

  // Rarity colors (for gifts, achievements, items)
  rarity_common?: string;
  rarity_uncommon?: string;
  rarity_rare?: string;
  rarity_epic?: string;
  rarity_legendary?: string;

  // Swipe/Match colors
  swipe_like?: string;
  swipe_superlike?: string;
  swipe_dislike?: string;

  // Navigation colors
  nav_icon_active?: string;
  nav_badge_bg?: string;

  // Post action colors (optional - defaults in CSS)
  action_like?: string;
  action_like_active?: string;
  action_comment?: string;
  action_view?: string;

  // Glass dropdown panel colors (menus, notifications, overlays)
  glass_dropdown_bg?: string;
  glass_dropdown_border?: string;
  glass_dropdown_highlight?: string;
}

export interface Typography {
  primary_font: string;
  secondary_font: string;
  accent_font: string;
  hero_title_size: number;
  page_title_size: number;
  section_title_size: number;
  body_text_size: number;
  small_text_size: number;
  title_weight: number;
  body_weight: number;
}

export interface SpacingSettings {
  xs?: string;
  sm?: string;
  md?: string;
  lg?: string;
  xl?: string;
  xxl?: string;
  xxxl?: string;
}

export interface RadiusSettings {
  none?: string;
  sm?: string;
  md?: string;
  lg?: string;
  xl?: string;
  xxl?: string;
  full?: string;
  button?: string;
  card?: string;
  input?: string;
  modal?: string;
  avatar?: string;
}

export interface ShadowSettings {
  sm?: string;
  md?: string;
  lg?: string;
  xl?: string;
  card?: string;
  dropdown?: string;
  modal?: string;
  glow?: string;
  glow_primary?: string;
  inner?: string;
}

export interface AnimationSettings {
  duration_fast?: string;
  duration_normal?: string;
  duration_slow?: string;
  easing_default?: string;
  easing_in?: string;
  easing_out?: string;
  hover_scale?: number;
  press_scale?: number;
}

export interface ComponentSettings {
  button_height_sm?: string;
  button_height_md?: string;
  button_height_lg?: string;
  button_padding_x?: string;
  button_font_weight?: number;
  input_height?: string;
  input_padding_x?: string;
  input_border_width?: string;
  card_padding?: string;
  card_gap?: string;
  nav_height_desktop?: string;
  nav_height_mobile?: string;
}

export interface LayoutSettings {
  max_container_width: string;
  content_padding: string;
  section_spacing: string;
  button_radius: string;
  card_radius: string;
  transition_duration: string;
  hover_scale: number;
  // Extended design tokens
  spacing?: SpacingSettings;
  radius?: RadiusSettings;
  shadows?: ShadowSettings;
  animation?: AnimationSettings;
  components?: ComponentSettings;
  // Active preset ID for persistence
  preset_id?: string;
}

export interface BrandingSettings {
  hero_title: string;
  hero_subtitle: string;
  primary_cta_text: string;
  secondary_cta_text: string;
  site_title: string;
  meta_description: string;
  logo_url?: string;
  mobile_logo_url?: string;
  show_site_name?: boolean;
  favicon_url?: string;
  robots_index?: boolean;
  robots_follow?: boolean;
}

export interface DesignConfiguration {
  enabled: boolean;
  theme_name: string;
  colors: ColorPalette;
  typography: Typography;
  layout: LayoutSettings;
  branding: BrandingSettings;
  custom_css?: string;
  character_visibility: {
    show_public_only_before_login: boolean;
  };
  show_ai_disclaimer?: boolean;
  feature_voice_input_enabled?: boolean;
  feature_games_enabled?: boolean;
}

export interface DesignConfigurationResponse extends DesignConfiguration {
  last_updated: string;
  updated_by?: string;
  version: number;
}

export interface DesignPreset {
  name: string;
  description: string;
  configuration: DesignConfiguration;
}

export interface DesignPresetListResponse {
  presets: DesignPreset[];
  current_preset?: string;
}

export const designAPI = {
  // Get full design configuration (public endpoint)
  getDesignConfiguration: () => apiClient.get<DesignConfiguration>('/design/configuration'),

  // Get admin design configuration with metadata (admin only)
  getAdminDesignConfiguration: () => apiClient.get<DesignConfigurationResponse>('/admin/design-configuration'),

  // Update full design configuration
  updateDesignConfiguration: (config: DesignConfiguration) =>
    apiClient.put<DesignConfigurationResponse>('/admin/design-configuration', config),

  // Get/update specific design components
  getColorPalette: () => apiClient.get<ColorPalette>('/admin/design-configuration/colors'),

  updateColorPalette: (colors: ColorPalette) =>
    apiClient.put<ColorPalette>('/admin/design-configuration/colors', colors),

  getTypography: () => apiClient.get<Typography>('/admin/design-configuration/typography'),

  updateTypography: (typography: Typography) =>
    apiClient.put<Typography>('/admin/design-configuration/typography', typography),

  getBranding: () => apiClient.get<BrandingSettings>('/admin/design-configuration/branding'),

  updateBranding: (branding: BrandingSettings) =>
    apiClient.put<BrandingSettings>('/admin/design-configuration/branding', branding),

  // Design presets
  getDesignPresets: () => apiClient.get<DesignPresetListResponse>('/admin/design-presets'),

  applyDesignPreset: (presetName: string) => apiClient.post(`/admin/design-presets/${presetName}/apply`),

  // Reset to defaults
  resetDesignConfiguration: () => apiClient.post('/admin/design-configuration/reset'),

  // Upload branding assets
  uploadLogo: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post<BrandingSettings>('/admin/design-configuration/branding/logo', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadMobileLogo: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post<BrandingSettings>('/admin/design-configuration/branding/mobile-logo', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadFavicon: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post<BrandingSettings>('/admin/design-configuration/branding/favicon', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};
